<template>
  <div class="restaurante-entregas-online">
    <div class="card card-custom gutter-b">
      <div class="card-header border-0 py-5">
        <h3 class="card-title align-items-start flex-column">
          <span class="card-label font-weight-bolder text-dark"
            >Entregas online</span
          >
          <span class="text-muted mt-2 font-weight-bold font-size-sm">
            Atribua pedidos prontos, acompanhe rota, baixa e retorno do
            entregador
          </span>
        </h3>
        <div class="card-toolbar">
          <span class="entregas-refresh-label">{{ lastRefreshLabel }}</span>
          <button
            class="btn btn-light-primary font-weight-bolder font-size-sm mr-2"
            type="button"
            @click="openCouriersModal"
          >
            Entregadores
          </button>
          <button
            class="btn btn-light-dark font-weight-bolder font-size-sm"
            type="button"
            @click="getEntregas"
          >
            Atualizar
          </button>
        </div>
      </div>

      <div class="card-body pt-0">
        <b-row class="align-items-end mb-6">
          <b-col md="3">
            <b-form-group label="Busca">
              <b-form-input
                v-model="filters.busca"
                placeholder="Pedido, cliente ou telefone"
                @keyup.enter="getEntregas"
              />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Status">
              <b-form-select
                v-model="filters.status"
                :options="statusFilterOptions"
                @change="getEntregas"
              />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Entregador">
              <b-form-select
                v-model="filters.entregador_id"
                :options="courierFilterOptions"
                @change="getEntregas"
              />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <button
              class="btn btn-primary btn-block font-weight-bolder"
              type="button"
              @click="getEntregas"
            >
              Filtrar entregas
            </button>
          </b-col>
        </b-row>

        <div class="entregas-resumo">
          <button
            v-for="metric in resumoCards"
            :key="metric.status || metric.key"
            type="button"
            :class="[
              'resumo-card',
              { 'resumo-card-active': filters.status === metric.status },
            ]"
            @click="setStatusFilter(metric.status)"
          >
            <span>{{ metric.label }}</span>
            <strong>{{ metric.value }}</strong>
          </button>
        </div>

        <div v-if="loading" class="empty-state">Carregando entregas...</div>
        <div v-else-if="!entregas.length" class="empty-state">
          Nenhum pedido pronto para entrega nos filtros selecionados.
        </div>

        <div v-else class="entregas-grid">
          <article
            v-for="entrega in entregas"
            :key="entrega.id"
            class="entrega-card"
          >
            <div class="entrega-card-top">
              <div>
                <small>Pedido</small>
                <strong>{{ pedidoCodigo(entrega) }}</strong>
              </div>
              <span :class="statusBadgeClass(entrega.status)">{{
                statusLabel(entrega.status)
              }}</span>
            </div>

            <div class="entrega-client">
              <strong>{{ clienteNome(entrega) }}</strong>
              <span>{{ telefone(entrega) }}</span>
            </div>

            <div class="entrega-address">
              <span>Endereço</span>
              <strong>{{ enderecoResumo(entrega) }}</strong>
            </div>

            <div class="entrega-meta">
              <div>
                <span>Total</span>
                <strong>{{
                  formatMoney(entrega.pedido && entrega.pedido.total)
                }}</strong>
              </div>
              <div>
                <span>Pagamento</span>
                <strong>{{ paymentLabel(entrega) }}</strong>
              </div>
              <div>
                <span>Entregador</span>
                <strong>{{ courierName(entrega) }}</strong>
              </div>
            </div>

            <div v-if="paymentPending(entrega)" class="payment-warning">
              Pagamento pendente para concluir o retorno.
            </div>

            <div
              v-if="entrega.status === 'aguardando_atribuicao'"
              class="assign-row"
            >
              <b-form-select
                v-model="assignForms[entrega.id]"
                :options="courierAssignOptions"
              />
              <button
                class="btn btn-primary font-weight-bolder"
                type="button"
                :disabled="actionLoading || !assignForms[entrega.id]"
                @click="atribuirEntrega(entrega)"
              >
                Atribuir
              </button>
            </div>

            <div class="actions-panel">
              <button
                v-if="entrega.status === 'atribuida'"
                class="btn btn-light-primary font-weight-bolder"
                type="button"
                :disabled="actionLoading"
                @click="postEntrega(entrega, 'aceitar')"
              >
                Aceitar entrega
              </button>
              <button
                v-if="['atribuida', 'aceita'].includes(entrega.status)"
                class="btn btn-primary font-weight-bolder"
                type="button"
                :disabled="actionLoading"
                @click="postEntrega(entrega, 'sair')"
              >
                Saiu para entrega
              </button>
              <button
                v-if="entrega.status === 'saiu_entrega'"
                class="btn btn-success font-weight-bolder"
                type="button"
                :disabled="actionLoading"
                @click="postEntrega(entrega, 'entregar')"
              >
                Marcar entregue
              </button>
              <button
                v-if="paymentPending(entrega)"
                class="btn btn-warning font-weight-bolder"
                type="button"
                :disabled="actionLoading"
                @click="openChargeModal(entrega)"
              >
                Registrar baixa
              </button>
              <button
                v-if="entrega.status === 'entregue'"
                class="btn btn-light-success font-weight-bolder"
                type="button"
                :disabled="actionLoading || paymentPending(entrega)"
                @click="openReturnModal(entrega)"
              >
                Retorno e concluir
              </button>
            </div>

            <div
              v-if="entrega.historicos && entrega.historicos.length"
              class="entrega-history"
            >
              <div
                v-for="historico in entrega.historicos.slice(-3)"
                :key="historico.id"
                class="history-row"
              >
                <span>{{ formatDate(historico.created_at) }}</span>
                <strong>{{ statusLabel(historico.status_para) }}</strong>
                <small>{{ historico.observacao }}</small>
              </div>
            </div>
          </article>
        </div>
      </div>
    </div>

    <b-modal
      id="modal-entrega-cobranca"
      title="Baixa de pagamento da entrega"
      size="lg"
      hide-footer
      no-close-on-backdrop
    >
      <div v-if="selected" class="entrega-modal">
        <div class="modal-summary">
          <div>
            <span>Pedido</span><strong>{{ pedidoCodigo(selected) }}</strong>
          </div>
          <div>
            <span>Cliente</span><strong>{{ clienteNome(selected) }}</strong>
          </div>
          <div>
            <span>Forma</span
            ><strong>{{ paymentMethodLabel(payment.metodo) }}</strong>
          </div>
          <div>
            <span>Total</span
            ><strong>{{
              formatMoney(
                payment.valor || (selected.pedido && selected.pedido.total)
              )
            }}</strong>
          </div>
        </div>

        <div v-if="isPixPayment" class="alert alert-warning mt-5">
          Confirme o Pix apenas depois de conferir o recebimento fora do
          cardápio online.
        </div>

        <b-row class="align-items-end mt-5">
          <b-col md="6">
            <b-form-group label="Valor recebido">
              <b-form-input
                v-model="chargeForm.valor_recebido"
                inputmode="decimal"
                placeholder="Ex.: 50,00"
                :disabled="isCardPayment || isPixPayment"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <div class="change-box">
              <span>Troco</span>
              <strong>{{ formatMoney(chargeChange) }}</strong>
            </div>
          </b-col>
        </b-row>

        <b-form-group label="Observação">
          <b-form-textarea
            v-model="chargeForm.observacao"
            rows="3"
            maxlength="255"
            placeholder="Ex.: recebido pelo entregador no retorno."
          />
        </b-form-group>

        <b-form-checkbox
          v-if="isPixPayment"
          v-model="chargeForm.confirmar_pix_manual"
          class="mb-5"
        >
          Confirmo que o Pix foi conferido manualmente.
        </b-form-checkbox>

        <div class="modal-actions">
          <button
            class="btn btn-light font-weight-bolder"
            type="button"
            :disabled="actionLoading"
            @click="$bvModal.hide('modal-entrega-cobranca')"
          >
            Cancelar
          </button>
          <button
            class="btn btn-success font-weight-bolder"
            type="button"
            :disabled="actionLoading || !canSubmitCharge"
            @click="registrarBaixa"
          >
            {{ actionLoading ? "Registrando..." : "Registrar pagamento" }}
          </button>
        </div>
      </div>
    </b-modal>

    <b-modal
      id="modal-entrega-retorno"
      title="Retorno do entregador"
      hide-footer
      no-close-on-backdrop
    >
      <div v-if="selected" class="entrega-modal">
        <p class="text-muted mb-5">
          Confirme o retorno depois que o entregador voltou para a loja. O
          pedido será concluído.
        </p>
        <b-form-group label="Observação do retorno">
          <b-form-textarea
            v-model="returnForm.observacao"
            rows="3"
            maxlength="255"
            placeholder="Ex.: pagamento entregue ao caixa, sem pendências."
          />
        </b-form-group>
        <div class="modal-actions">
          <button
            class="btn btn-light font-weight-bolder"
            type="button"
            :disabled="actionLoading"
            @click="$bvModal.hide('modal-entrega-retorno')"
          >
            Cancelar
          </button>
          <button
            class="btn btn-success font-weight-bolder"
            type="button"
            :disabled="actionLoading"
            @click="registrarRetorno"
          >
            {{ actionLoading ? "Concluindo..." : "Registrar retorno" }}
          </button>
        </div>
      </div>
    </b-modal>

    <b-modal id="modal-entregadores" title="Entregadores" size="lg" hide-footer>
      <div class="couriers-modal">
        <b-row class="align-items-end">
          <b-col md="5">
            <b-form-group label="Usuário">
              <b-form-select
                v-model="courierForm.usuario_id"
                :options="userOptions"
              />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Nome exibido">
              <b-form-input
                v-model="courierForm.nome_exibicao"
                placeholder="Opcional"
              />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <button
              class="btn btn-primary btn-block font-weight-bolder"
              type="button"
              :disabled="actionLoading || !courierForm.usuario_id"
              @click="salvarEntregador"
            >
              Vincular
            </button>
          </b-col>
        </b-row>

        <div v-if="!entregadores.length" class="empty-state empty-state-small">
          Nenhum entregador vinculado.
        </div>
        <div
          v-for="entregador in entregadores"
          :key="entregador.id"
          class="courier-row"
        >
          <div>
            <strong>{{ entregador.nome }}</strong>
            <span>{{ entregador.usuario && entregador.usuario.usuario }}</span>
          </div>
          <button
            class="btn btn-sm font-weight-bolder"
            :class="entregador.ativo ? 'btn-light-danger' : 'btn-light-success'"
            type="button"
            :disabled="actionLoading"
            @click="toggleEntregador(entregador)"
          >
            {{ entregador.ativo ? "Pausar" : "Ativar" }}
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions } from "vuex";

const STATUS_LABELS = {
  aguardando_atribuicao: "Aguardando atribuição",
  atribuida: "Atribuída",
  aceita: "Aceita",
  saiu_entrega: "Em rota",
  entregue: "Entregue",
  retornou: "Retorno concluído",
  cancelada: "Cancelada",
};

const PAYMENT_METHOD_LABELS = {
  DINHEIRO_ENTREGA: "Dinheiro na entrega",
  CARTAO_ENTREGA: "Cartão na entrega",
  PIX: "Pix",
};

export default {
  name: "RestauranteEntregasOnline",
  data() {
    return {
      loading: false,
      actionLoading: false,
      entregas: [],
      entregadores: [],
      usuarios: [],
      resumo: {},
      entregasInterval: null,
      lastRefresh: null,
      filters: {
        busca: "",
        status: "",
        entregador_id: "",
      },
      assignForms: {},
      selected: null,
      chargeForm: {
        valor_recebido: "",
        observacao: "",
        confirmar_pix_manual: false,
      },
      returnForm: {
        observacao: "",
      },
      courierForm: {
        usuario_id: "",
        nome_exibicao: "",
      },
    };
  },
  computed: {
    lastRefreshLabel() {
      return this.lastRefresh
        ? `atualizado ${this.formatDate(this.lastRefresh)}`
        : "";
    },
    statusFilterOptions() {
      return [
        { value: "", text: "Todos" },
        ...Object.keys(STATUS_LABELS).map((status) => ({
          value: status,
          text: STATUS_LABELS[status],
        })),
      ];
    },
    courierFilterOptions() {
      return [
        { value: "", text: "Todos" },
        ...this.entregadores.map((entregador) => ({
          value: entregador.id,
          text: entregador.nome,
        })),
      ];
    },
    courierAssignOptions() {
      return [
        { value: "", text: "Selecionar entregador" },
        ...this.entregadores
          .filter((entregador) => entregador.ativo !== false)
          .map((entregador) => ({
            value: entregador.id,
            text: entregador.nome,
          })),
      ];
    },
    userOptions() {
      return [
        { value: "", text: "Selecionar usuário" },
        ...this.usuarios.map((usuario) => ({
          value: usuario.id_usuario,
          text: usuario.nome || usuario.usuario,
        })),
      ];
    },
    resumoCards() {
      return [
        {
          key: "aguardando",
          status: "aguardando_atribuicao",
          label: "Aguardando",
          value: this.resumo.aguardando_atribuicao || 0,
        },
        {
          key: "atribuidas",
          status: "atribuida",
          label: "Atribuídas",
          value: this.resumo.atribuidas || 0,
        },
        {
          key: "rota",
          status: "saiu_entrega",
          label: "Em rota",
          value: this.resumo.em_rota || 0,
        },
        {
          key: "entregues",
          status: "entregue",
          label: "Entregues",
          value: this.resumo.entregues || 0,
        },
        {
          key: "retornos",
          status: "retornou",
          label: "Retornos",
          value: this.resumo.retornos || 0,
        },
      ];
    },
    payment() {
      return (
        (this.selected &&
          this.selected.pedido &&
          this.selected.pedido.pagamento) ||
        {}
      );
    },
    isPixPayment() {
      return this.payment.metodo === "PIX";
    },
    isCardPayment() {
      return this.payment.metodo === "CARTAO_ENTREGA";
    },
    chargeValue() {
      return this.toNumber(
        this.payment.valor ||
          (this.selected &&
            this.selected.pedido &&
            this.selected.pedido.total) ||
          0
      );
    },
    chargeReceived() {
      if (
        this.isCardPayment ||
        this.isPixPayment ||
        !this.chargeForm.valor_recebido
      ) {
        return this.chargeValue;
      }
      return this.toNumber(this.chargeForm.valor_recebido);
    },
    chargeChange() {
      if (this.payment.metodo !== "DINHEIRO_ENTREGA") {
        return 0;
      }
      return Math.max(0, this.chargeReceived - this.chargeValue);
    },
    canSubmitCharge() {
      if (!this.selected || !this.paymentPending(this.selected)) {
        return false;
      }
      if (this.isPixPayment && !this.chargeForm.confirmar_pix_manual) {
        return false;
      }
      return this.chargeReceived >= this.chargeValue;
    },
  },
  mounted() {
    this.getEntregas();
    // Polling leve (20s) para manter a fila atualizada sem干预 manual. Pausa
    // quando a aba esta ociosa para nao gerar carga desnecessaria.
    this.entregasInterval = setInterval(() => {
      if (!document.hidden && !this.actionLoading) {
        this.getEntregas();
      }
    }, 20000);
  },
  beforeDestroy() {
    if (this.entregasInterval) {
      clearInterval(this.entregasInterval);
    }
  },
  methods: {
    ...mapActions(["addSuccess", "addErro"]),
    unwrap(response) {
      const payload =
        response && response.data !== undefined ? response.data : response;
      if (payload && payload.success === true) return payload.data;
      if (payload && payload.status === true) return payload.data;
      return payload;
    },
    errorMessage(error, fallback) {
      return error && error.response && error.response.data
        ? error.response.data.msg || error.response.data.message || fallback
        : fallback;
    },
    async getEntregas() {
      this.loading = true;
      try {
        const response = await this.$http.get("restaurante/entregas-online", {
          params: this.filters,
        });
        const data = this.unwrap(response) || {};
        this.entregas = data.entregas || [];
        this.entregadores = data.entregadores || [];
        this.resumo = data.resumo || {};
        this.entregas.forEach((entrega) => {
          if (!this.assignForms[entrega.id]) {
            this.$set(
              this.assignForms,
              entrega.id,
              entrega.entregador ? entrega.entregador.id : ""
            );
          }
        });
        this.lastRefresh = new Date().toISOString();
      } catch (error) {
        this.addErro({
          title: this.errorMessage(
            error,
            "Não foi possível carregar entregas."
          ),
        });
      } finally {
        this.loading = false;
      }
    },
    async getUsuarios() {
      const response = await this.$http.get("usuario/get-usuarios");
      this.usuarios = this.unwrap(response) || [];
    },
    async openCouriersModal() {
      try {
        await Promise.all([this.getUsuarios(), this.getEntregadores()]);
        this.$bvModal.show("modal-entregadores");
      } catch (error) {
        this.addErro({
          title: this.errorMessage(
            error,
            "Não foi possível carregar entregadores."
          ),
        });
      }
    },
    async getEntregadores() {
      const response = await this.$http.get(
        "restaurante/entregas-online/entregadores"
      );
      this.entregadores = this.unwrap(response) || [];
    },
    async salvarEntregador() {
      this.actionLoading = true;
      try {
        await this.$http.post("restaurante/entregas-online/entregadores", {
          usuario_id: this.courierForm.usuario_id,
          nome_exibicao: this.courierForm.nome_exibicao,
          ativo: true,
        });
        this.courierForm = { usuario_id: "", nome_exibicao: "" };
        await this.getEntregadores();
        await this.getEntregas();
        this.addSuccess({ title: "Entregador vinculado." });
      } catch (error) {
        this.addErro({
          title: this.errorMessage(
            error,
            "Não foi possível salvar o entregador."
          ),
        });
      } finally {
        this.actionLoading = false;
      }
    },
    async toggleEntregador(entregador) {
      this.actionLoading = true;
      try {
        await this.$http.post(
          `restaurante/entregas-online/entregadores/${entregador.id}/status`,
          { ativo: !entregador.ativo }
        );
        await this.getEntregadores();
        await this.getEntregas();
        this.addSuccess({
          title: entregador.ativo
            ? "Entregador pausado."
            : "Entregador ativado.",
        });
      } catch (error) {
        this.addErro({
          title: this.errorMessage(
            error,
            "Não foi possível alterar o entregador."
          ),
        });
      } finally {
        this.actionLoading = false;
      }
    },
    async atribuirEntrega(entrega) {
      this.actionLoading = true;
      try {
        const response = await this.$http.post(
          "restaurante/entregas-online/atribuir",
          {
            pedido_online_id: entrega.pedido_online_id,
            entregador_id: this.assignForms[entrega.id],
          }
        );
        this.upsertEntrega(this.unwrap(response));
        await this.getEntregas();
        this.addSuccess({ title: "Entrega atribuída." });
      } catch (error) {
        this.addErro({
          title: this.errorMessage(
            error,
            "Não foi possível atribuir a entrega."
          ),
        });
      } finally {
        this.actionLoading = false;
      }
    },
    async postEntrega(entrega, acao) {
      this.actionLoading = true;
      try {
        const response = await this.$http.post(
          `restaurante/entregas-online/${entrega.id}/${acao}`
        );
        this.upsertEntrega(this.unwrap(response));
        await this.getEntregas();
        this.addSuccess({ title: "Entrega atualizada." });
      } catch (error) {
        this.addErro({
          title: this.errorMessage(
            error,
            "Não foi possível atualizar a entrega."
          ),
        });
      } finally {
        this.actionLoading = false;
      }
    },
    openChargeModal(entrega) {
      this.selected = entrega;
      const valor =
        entrega.pedido && entrega.pedido.pagamento
          ? entrega.pedido.pagamento.valor
          : entrega.pedido.total;
      this.chargeForm = {
        valor_recebido: this.formatInputMoney(valor),
        observacao: "",
        confirmar_pix_manual: false,
      };
      this.$bvModal.show("modal-entrega-cobranca");
    },
    async registrarBaixa() {
      this.actionLoading = true;
      try {
        const response = await this.$http.post(
          `restaurante/entregas-online/${this.selected.id}/pagamento/baixar`,
          {
            valor_recebido: this.chargeReceived,
            observacao: this.chargeForm.observacao,
            confirmar_pix_manual: this.chargeForm.confirmar_pix_manual,
          }
        );
        this.upsertEntrega(this.unwrap(response));
        await this.getEntregas();
        this.$bvModal.hide("modal-entrega-cobranca");
        this.addSuccess({ title: "Pagamento registrado." });
      } catch (error) {
        this.addErro({
          title: this.errorMessage(
            error,
            "Não foi possível registrar a baixa."
          ),
        });
      } finally {
        this.actionLoading = false;
      }
    },
    openReturnModal(entrega) {
      this.selected = entrega;
      this.returnForm = { observacao: "" };
      this.$bvModal.show("modal-entrega-retorno");
    },
    async registrarRetorno() {
      this.actionLoading = true;
      try {
        const response = await this.$http.post(
          `restaurante/entregas-online/${this.selected.id}/retornar`,
          this.returnForm
        );
        this.upsertEntrega(this.unwrap(response));
        await this.getEntregas();
        this.$bvModal.hide("modal-entrega-retorno");
        this.addSuccess({ title: "Retorno registrado." });
      } catch (error) {
        this.addErro({
          title: this.errorMessage(
            error,
            "Não foi possível registrar o retorno."
          ),
        });
      } finally {
        this.actionLoading = false;
      }
    },
    upsertEntrega(entrega) {
      if (!entrega || !entrega.id) return;
      const index = this.entregas.findIndex((item) => item.id === entrega.id);
      if (index >= 0) this.$set(this.entregas, index, entrega);
      else this.entregas.unshift(entrega);
    },
    setStatusFilter(status) {
      this.filters.status = this.filters.status === status ? "" : status;
      this.getEntregas();
    },
    pedidoCodigo(entrega) {
      return entrega && entrega.pedido ? entrega.pedido.codigo_publico : "-";
    },
    clienteNome(entrega) {
      return entrega && entrega.pedido
        ? entrega.pedido.cliente_nome || "Cliente não informado"
        : "-";
    },
    telefone(entrega) {
      return entrega && entrega.pedido
        ? entrega.pedido.cliente_telefone || "-"
        : "-";
    },
    courierName(entrega) {
      return entrega && entrega.entregador
        ? entrega.entregador.nome
        : "Não atribuído";
    },
    paymentPending(entrega) {
      return !!(
        entrega &&
        entrega.pedido &&
        entrega.pedido.pagamento &&
        entrega.pedido.pagamento.status !== "pago"
      );
    },
    paymentLabel(entrega) {
      const pagamento =
        entrega && entrega.pedido ? entrega.pedido.pagamento : null;
      if (!pagamento) return "-";
      return `${this.paymentMethodLabel(pagamento.metodo)} · ${
        pagamento.status === "pago" ? "Pago" : "Pendente"
      }`;
    },
    paymentMethodLabel(method) {
      return PAYMENT_METHOD_LABELS[method] || method || "-";
    },
    enderecoResumo(entrega) {
      const endereco =
        entrega && entrega.pedido ? entrega.pedido.endereco : null;
      if (!endereco) return "Endereço não informado";
      return [
        endereco.logradouro || endereco.rua,
        endereco.numero,
        endereco.bairro,
        endereco.cidade,
      ]
        .filter(Boolean)
        .join(", ");
    },
    statusLabel(status) {
      return STATUS_LABELS[status] || status || "-";
    },
    statusBadgeClass(status) {
      return [
        "status-badge",
        {
          "status-badge-warning": [
            "aguardando_atribuicao",
            "atribuida",
            "aceita",
          ].includes(status),
          "status-badge-primary": status === "saiu_entrega",
          "status-badge-success": ["entregue", "retornou"].includes(status),
        },
      ];
    },
    formatMoney(value) {
      const amount = Number(value || 0);
      return amount.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    formatInputMoney(value) {
      return Number(value || 0).toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    toNumber(value) {
      if (typeof value === "number") return value;
      const cleaned = String(value || "0")
        .replace(/[^0-9,.-]/g, "")
        .replace(".", "")
        .replace(",", ".");
      return Number(cleaned) || 0;
    },
    formatDate(value) {
      if (!value) return "-";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "-";
      return date.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
};
</script>

<style scoped>
.restaurante-entregas-online {
  color: #1f2437;
}
.entregas-refresh-label {
  color: #8b90a8;
  font-size: 12px;
  font-weight: 700;
  margin-right: 12px;
}
.entregas-resumo {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 24px;
}
.resumo-card {
  background: #fff;
  border: 1px solid #e6e9f2;
  border-radius: 8px;
  color: #7d839d;
  min-height: 84px;
  padding: 16px;
  text-align: left;
}
.resumo-card strong {
  color: #1f2437;
  display: block;
  font-size: 28px;
  margin-top: 8px;
}
.resumo-card-active {
  border-color: #f06f61;
  box-shadow: 0 10px 24px rgba(240, 111, 97, 0.12);
}
.entregas-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 16px;
}
.entrega-card {
  background: #fff;
  border: 1px solid #e6e9f2;
  border-radius: 8px;
  padding: 18px;
}
.entrega-card-top,
.entrega-meta,
.assign-row,
.modal-actions,
.courier-row {
  display: flex;
  gap: 12px;
}
.entrega-card-top,
.modal-actions,
.courier-row {
  align-items: center;
  justify-content: space-between;
}
.entrega-card small,
.entrega-meta span,
.entrega-address span,
.modal-summary span {
  color: #858ba5;
  display: block;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}
.entrega-card strong {
  color: #20243a;
}
.entrega-client,
.entrega-address,
.entrega-meta,
.assign-row,
.actions-panel,
.entrega-history {
  margin-top: 16px;
}
.entrega-client span {
  color: #7d839d;
  display: block;
}
.entrega-address {
  background: #f6f8fc;
  border-radius: 8px;
  padding: 12px;
}
.entrega-meta {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}
.payment-warning {
  background: #fff6df;
  border: 1px solid #ffd77a;
  border-radius: 8px;
  color: #966900;
  font-weight: 700;
  margin-top: 14px;
  padding: 10px 12px;
}
.actions-panel {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.status-badge {
  background: #eef1f8;
  border-radius: 999px;
  color: #606783;
  font-size: 12px;
  font-weight: 800;
  padding: 7px 10px;
}
.status-badge-warning {
  background: #fff0d8;
  color: #9b6500;
}
.status-badge-primary {
  background: #e1f0ff;
  color: #2573c7;
}
.status-badge-success {
  background: #dff8ed;
  color: #198754;
}
.empty-state {
  align-items: center;
  background: #f5f7fb;
  border-radius: 8px;
  color: #7d839d;
  display: flex;
  font-weight: 800;
  justify-content: center;
  min-height: 120px;
  padding: 20px;
  text-align: center;
}
.empty-state-small {
  min-height: 72px;
}
.history-row {
  border-top: 1px solid #edf0f6;
  display: grid;
  gap: 8px;
  grid-template-columns: 70px 120px 1fr;
  padding: 8px 0;
}
.history-row span,
.history-row small {
  color: #7d839d;
}
.modal-summary {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}
.modal-summary > div,
.change-box {
  background: #f6f8fc;
  border-radius: 8px;
  padding: 14px;
}
.change-box span {
  color: #858ba5;
  display: block;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}
.change-box strong {
  color: #20243a;
  display: block;
  font-size: 22px;
}
.courier-row {
  border-top: 1px solid #edf0f6;
  padding: 12px 0;
}
.courier-row span {
  color: #7d839d;
  display: block;
}
@media (max-width: 900px) {
  .entregas-resumo,
  .entregas-grid,
  .modal-summary,
  .entrega-meta {
    grid-template-columns: 1fr;
  }
  .assign-row {
    flex-direction: column;
  }
}
</style>
